// ════════════════════════════════════════════════════════════════════════
//  The trainer's state machine — no rendering, no JSX.
//
//  Split out so it can be reasoned about and tested on its own: everything that
//  decides *what happens* lives here, everything that decides *what it looks
//  like* lives in the components next to it.
// ════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from "react";
import {
  fingerFor,
  nextStage,
  resultOf,
  startStage,
  target,
  typeKey,
  type Bundle,
  type Finger,
  type Lesson,
  type StageResult,
  type StageState,
} from "@otto/lehrgang";

/**
 * Was zu tun ist, wenn der Großbuchstabe auf dem anderen Weg entstand. Text und
 * nicht bloß ein Merker: die Lern-App soll ihn zeigen können, ohne die Regel zu
 * kennen.
 */
const WAY_HINT: Record<"shift" | "capsLock", string> = {
  shift: "Die Feststelltaste ⇪ ist an. Schalt sie aus und halte die Umschalttaste ⇧.",
  capsLock: "Nimm die Feststelltaste ⇪ – dann musst du nichts festhalten.",
};

export type Phase =
  | { readonly name: "intro" }
  | { readonly name: "practice"; readonly state: StageState }
  | { readonly name: "done" };

export type Trainer = {
  readonly phase: Phase;
  readonly stage: Lesson["stages"][number];
  readonly stageIndex: number;
  readonly results: readonly StageResult[];
  /** The character the child is expected to type, or null outside practice. */
  readonly expected: string | null;
  readonly finger: Finger | null;
  /** Last key pressed — drives the brief flash on the on-screen keyboard. */
  readonly lastKey: string | null;
  /** Counts misses; a change restarts the shake animation. */
  readonly missCount: number;
  readonly shaking: boolean;
  /**
   * Was zu tun ist, wenn der letzte Fehler am Weg zum Großbuchstaben lag –
   * sonst `null`. Ohne diesen Satz bliebe nur ein stummes Wackeln, und das Kind
   * sucht den Fehler beim Buchstaben, wo keiner ist.
   */
  readonly wayHint: string | null;
  readonly clearShake: () => void;
  readonly begin: () => void;
  readonly handleKey: (key: string, modifiers?: { readonly shiftKey?: boolean }) => void;
  readonly restart: () => void;
};

export function useTrainer({
  bundle,
  lesson,
  onStageDone,
}: {
  bundle: Bundle;
  lesson: Lesson;
  // `| undefined` explicitly: with exactOptionalPropertyTypes an optional
  // prop cannot be forwarded from a caller that also has it optional.
  onStageDone?: ((result: StageResult) => void) | undefined;
}): Trainer {
  const [phase, setPhase] = useState<Phase>({ name: "intro" });
  const [results, setResults] = useState<readonly StageResult[]>([]);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [missCount, setMissCount] = useState(0);
  const [shaking, setShaking] = useState(false);

  const [wayHint, setWayHint] = useState<string | null>(null);

  const stageIndex = results.length;
  const stage = lesson.stages[Math.min(stageIndex, lesson.stages.length - 1)]!;
  const expected = phase.name === "practice" ? target(phase.state) : null;
  const finger = expected === null ? null : fingerFor(bundle, expected);

  const begin = useCallback(() => {
    setPhase({ name: "practice", state: startStage(stage) });
  }, [stage]);

  const handleKey = useCallback(
    (key: string, modifiers: { readonly shiftKey?: boolean } = {}) => {
      if (phase.name === "intro") {
        if (key === " " || key === "Enter") begin();
        return;
      }
      if (phase.name !== "practice") return;

      const { state, stroke, wrongWay } = typeKey(phase.state, key, {
        ...modifiers,
        uppercaseWith: lesson.uppercaseWith,
      });
      if (stroke === "ignored") return;

      setWayHint(wrongWay === undefined ? null : WAY_HINT[wrongWay]);

      setLastKey(key.length === 1 ? key.toLowerCase() : null);
      if (stroke === "miss") {
        setMissCount((n) => n + 1);
        setShaking(true);
      }

      if (stroke !== "complete") {
        setPhase({ name: "practice", state });
        return;
      }

      const result = resultOf(state, bundle.starThresholds);
      setResults([...results, result]);
      onStageDone?.(result);

      const following = nextStage(lesson, state.stageKey);
      setPhase(
        following ? { name: "practice", state: startStage(following) } : { name: "done" },
      );
    },
    [phase, begin, bundle.starThresholds, results, lesson, onStageDone],
  );

  // One listener on window instead of an input field: on the iPad an <input>
  // would flip the on-screen keyboard over half the exercise.
  //
  // Registered once and calling the current handler through a ref. Attached to
  // `handleKey` directly it was removed and re-added on every state change, and
  // a keystroke landing in exactly that moment was lost.
  const handleKeyRef = useRef(handleKey);
  useEffect(() => {
    handleKeyRef.current = handleKey;
  }, [handleKey]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Space would scroll the page otherwise.
      if (e.key === " ") e.preventDefault();
      handleKeyRef.current(e.key, { shiftKey: e.shiftKey });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // The pressed key keeps flashing briefly, as in the prototype.
  useEffect(() => {
    if (lastKey === null) return;
    const timer = setTimeout(() => setLastKey(null), 160);
    return () => clearTimeout(timer);
  }, [lastKey]);

  const restart = useCallback(() => {
    setResults([]);
    setMissCount(0);
    setShaking(false);
    setWayHint(null);
    setPhase({ name: "intro" });
  }, []);

  const clearShake = useCallback(() => setShaking(false), []);

  return {
    phase,
    stage,
    stageIndex,
    results,
    expected,
    finger,
    lastKey,
    missCount,
    shaking,
    wayHint,
    clearShake,
    begin,
    handleKey,
    restart,
  };
}
